"use client";

import { c as _c } from "react/compiler-runtime";
import { RiAddLine, RiCloseLine, RiEditLine } from "react-icons/ri";
import Badge from "@/components/ui/badge";
import { cx } from "@/lib/tailwind-utils";
import { useDiffContext } from "./diff-context";
import { type DiffStatus } from "./types";
interface DiffStepWrapperProps {
  stepId: string;
  children: React.ReactNode;
}
const STATUS_STYLES: Record<DiffStatus, {
  ring: string;
  badge: React.ReactNode;
} | null> = {
  added: {
    ring: "ring-2 ring-status-success-text/60",
    badge: <Badge variant="success" size="xs" icon={RiAddLine}>
        Added
      </Badge>
  },
  removed: {
    ring: "ring-2 ring-status-error-text/60",
    badge: <Badge variant="error" size="xs" icon={RiCloseLine}>
        Removed
      </Badge>
  },
  modified: {
    ring: "ring-2 ring-status-warning-text/60",
    badge: <Badge variant="warning" size="xs" icon={RiEditLine}>
        Modified
      </Badge>
  },
  unchanged: null
};

/**
 * Wraps an existing StepCard with diff decorations (colored ring + badge).
 *
 * Reads from DiffContext for the annotation. The child StepCard is
 * rendered completely unmodified. Field-level change details are shown
 * in the popover (via DiffPopoverHeader), not here.
 */
export function DiffStepWrapper(t0) {
  const $ = _c(21);
  const {
    stepId,
    children
  } = t0;
  const {
    annotations
  } = useDiffContext();
  let t1;
  if ($[0] !== annotations || $[1] !== stepId) {
    t1 = annotations.get(stepId);
    $[0] = annotations;
    $[1] = stepId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const annotation = t1;
  if (!annotation || annotation.status === "unchanged") {
    let t2;
    if ($[3] !== children) {
      t2 = <>{children}</>;
      $[3] = children;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  const style = STATUS_STYLES[annotation.status];
  if (!style) {
    let t2;
    if ($[5] !== children) {
      t2 = <>{children}</>;
      $[5] = children;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  const t2 = annotation.status === "removed" && "[&_span]:line-through [&_h4]:line-through";
  let t3;
  if ($[7] !== t2) {
    t3 = cx(t2);
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== style.badge) {
    t4 = <div className="mb-1 flex justify-end">{style.badge}</div>;
    $[9] = style.badge;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = annotation.status === "removed" && "opacity-60";
  let t6;
  if ($[11] !== style.ring || $[12] !== t5) {
    t6 = cx("rounded-lg w-fit", style.ring, t5);
    $[11] = style.ring;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== children || $[15] !== t6) {
    t7 = <div className={t6}>{children}</div>;
    $[14] = children;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t7) {
    t8 = <div className="relative flex flex-col items-center"><div className={t3}>{t4}{t7}</div></div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}